// Types et interfaces
type URLPattern = 'basic' | 'complete';
type Protocol = 'http:' | 'https:' | 'ftp:' | 'ftps:' | string;

interface ValidationOptions {
    allowedProtocols?: Protocol[];
    requireProtocol?: boolean;
    pattern?: URLPattern;
}

interface URLDetails {
    valid: true;
    protocol: string;
    hostname: string;
    port: string;
    pathname: string;
    search: string;
    hash: string;
    href: string;
}

interface URLError {
    valid: false;
    error: string;
}

type URLValidationResult = URLDetails | URLError;

interface ValidationResult {
    valid: boolean;
    error?: string;
    parsed?: URL;
}

// Classe principale
class URLValidator {
    private static readonly patterns: Record<URLPattern, RegExp> = {
        basic: /^https?:\/\/.+/i,
        complete: /^(https?|ftp):\/\/(([a-z\d]([a-z\d-]*[a-z\d])*\.)+[a-z]{2,}|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(\#[-a-z\d_]*)?$/i
    };

    private static readonly defaultOptions: Required<ValidationOptions> = {
        allowedProtocols: ['http:', 'https:'],
        requireProtocol: true,
        pattern: 'complete'
    };

    /**
     * Valide une URL selon les options spécifiées
     * @param url - L'URL à valider
     * @param options - Options de validation
     * @returns true si l'URL est valide, false sinon
     */
    static isValid(url: string, options: ValidationOptions = {}): boolean {
        const opts = { ...this.defaultOptions, ...options };

        // Test regex d'abord
        if (!this.patterns[opts.pattern].test(url)) {
            return false;
        }

        try {
            const urlObj = new URL(url);

            if (opts.requireProtocol && !opts.allowedProtocols.includes(urlObj.protocol as Protocol)) {
                return false;
            }

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Valide une URL et retourne les détails ou l'erreur
     * @param url - L'URL à valider
     * @param options - Options de validation
     * @returns Objet avec les détails de validation
     */
    static validate(url: string, options: ValidationOptions = {}): ValidationResult {
        const opts = { ...this.defaultOptions, ...options };

        // Test regex d'abord
        if (!this.patterns[opts.pattern].test(url)) {
            return {
                valid: false,
                error: `L'URL ne correspond pas au pattern ${opts.pattern}`
            };
        }

        try {
            const urlObj = new URL(url);

            if (opts.requireProtocol && !opts.allowedProtocols.includes(urlObj.protocol as Protocol)) {
                return {
                    valid: false,
                    error: `Protocole non autorisé. Protocoles acceptés: ${opts.allowedProtocols.join(', ')}`
                };
            }

            // Vérifications supplémentaires
            if (urlObj.hostname.length > 253) {
                return {
                    valid: false,
                    error: "Nom de domaine trop long (max 253 caractères)"
                };
            }

            return { valid: true, parsed: urlObj };
        } catch (error) {
            return {
                valid: false,
                error: error instanceof Error ? error.message : 'Erreur inconnue'
            };
        }
    }

    /**
     * Extrait les détails d'une URL
     * @param url - L'URL à analyser
     * @returns Détails de l'URL ou erreur
     */
    static getDetails(url: string): URLValidationResult {
        try {
            const urlObj = new URL(url);
            return {
                valid: true,
                protocol: urlObj.protocol,
                hostname: urlObj.hostname,
                port: urlObj.port,
                pathname: urlObj.pathname,
                search: urlObj.search,
                hash: urlObj.hash,
                href: urlObj.href
            };
        } catch (error) {
            return {
                valid: false,
                error: error instanceof Error ? error.message : 'URL invalide'
            };
        }
    }

    /**
     * Valide spécifiquement les URLs HTTP/HTTPS
     * @param url - L'URL à valider
     * @returns true si l'URL HTTP/HTTPS est valide
     */
    static isHTTPURL(url: string): boolean {
        return this.isValid(url, {
            allowedProtocols: ['http:', 'https:'],
            pattern: 'complete'
        });
    }

    /**
     * Valide spécifiquement les URLs HTTPS
     * @param url - L'URL à valider
     * @returns true si l'URL HTTPS est valide
     */
    static isHTTPSURL(url: string): boolean {
        return this.isValid(url, {
            allowedProtocols: ['https:'],
            pattern: 'complete'
        });
    }

    /**
     * Normalise une URL (ajoute le protocole si manquant)
     * @param url - L'URL à normaliser
     * @param defaultProtocol - Protocole par défaut à utiliser
     * @returns URL normalisée ou null si invalide
     */
    static normalize(url: string, defaultProtocol: string = 'https:'): string | null {
        try {
            // Si pas de protocole, on l'ajoute
            if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url)) {
                url = `${defaultProtocol}//${url}`;
            }

            const urlObj = new URL(url);
            return urlObj.href;
        } catch {
            return null;
        }
    }

    /**
     * Vérifie si une chaîne ressemble à un domaine valide
     * @param domain - Le domaine à vérifier
     * @returns true si le domaine semble valide
     */
    static isDomain(domain: string): boolean {
        const domainRegex = /^([a-z\d]([a-z\d-]*[a-z\d])*\.)+[a-z]{2,}$/i;
        return domainRegex.test(domain) && domain.length <= 253;
    }

    static getParams(): URLSearchParams | null {
        try {
            const urlObj = new URL(window.location.href);
            return urlObj.searchParams;
        } catch {
            return null;
        }
    }
    static getByParam(param: string): string | null {

        try {
            const urlObj = new URL(window.location.href);
            return urlObj.searchParams.get(param);
        } catch {
            return null;
        }
    }
}

// Fonctions utilitaires en tant que raccourcis
const isValidURL = (url: string, options?: ValidationOptions): boolean =>
    URLValidator.isValid(url, options);

const validateURL = (url: string, options?: ValidationOptions): ValidationResult =>
    URLValidator.validate(url, options);

const getURLDetails = (url: string): URLValidationResult =>
    URLValidator.getDetails(url);

// Export de la classe et des fonctions utilitaires
export {
    URLValidator,
    isValidURL,
    validateURL,
    getURLDetails,
    type ValidationOptions,
    type URLValidationResult,
    type ValidationResult,
    type URLDetails,
    type URLError,
    type Protocol,
    type URLPattern
};

export default URLValidator;
// console.log(URLValidator.isValid("http://example.com?tata=ss&toto=1#ancre")); // true
// console.log(URLValidator.isValid("not-a-url")); // false
//console.log(URLValidator.isValid(null));

